/**
 * Deferred Run Tools for FlowRide Frontend
 * Provides utility functions for deferred execution with animation frames and timeouts.
 * Optimizes performance by preventing duplicate executions and managing execution queues.
 */

/** Cache map for tracking running timeout requests */
const runningTimeoutCacheMap = new Map<string, [number, NodeJS.Timeout]>();

/**
 * Run a function on next timeout if one is not already queued, else replace previous one planned
 * Avoids launching a function too often and refreshing the interface too fast, saving CPU
 * @param name - Unique identifier for the execution
 * @param runLater - Function to run on timeout
 * @param timeout - Timeout duration in milliseconds
 * @param args - Function arguments
 */
export function runOnTimeoutIfNotQueued( name: string, runLater: ( ...args: unknown[] ) => void, timeout: number, args?: unknown[] ) {
    if ( !runningTimeoutCacheMap.has( name ) ) {
        runningTimeoutCacheMap.set( name, [
            1,
            setTimeout( () => {
                const timeoutEntry = runningTimeoutCacheMap.get( name );
                if ( timeoutEntry != null ) {
                    timeoutEntry[0] = 2;
                    try {
                        if ( args != null && args.length > 0 ) {
                            switch ( args.length ) {
                                case 1:
                                    runLater( args[0] );
                                    console.log( "runLater ", args[0] );
                                    break;

                                case 2:
                                    runLater( args[0], args[1] );
                                    break;

                                case 3:
                                    runLater( args[0], args[1], args[2] );
                                    break;

                                case 4:
                                    runLater( args[0], args[1], args[2], args[3] );
                                    break;

                                case 5:
                                    runLater( args[0], args[1], args[2], args[3], args[4] );
                                    break;
                            }
                        } else {
                            runLater();
                        }
                    } finally {
                        runningTimeoutCacheMap.delete( name );
                    }
                }
            }, timeout ),
        ] );
    } else {
        const timeoutEntry = runningTimeoutCacheMap.get( name );

        // if the animation frame computation has not been started, cancel it and replace with the latest one
        if ( timeoutEntry != null && timeoutEntry[0] === 1 ) {
            clearTimeout( timeoutEntry[1] );
            runningTimeoutCacheMap.delete( name );
            runOnTimeoutIfNotQueued( name, runLater, timeout, args );
        }
    }
}
